export const DEFAULT_SIDE_BORDER = {
  width: 0,
  style: "none",
  color: "#e2e8f0",
  radius: 0,
};

export const DEFAULT_BORDER = {
  advanced: false,
  width: 0,
  style: "none",
  color: "#e2e8f0",
  radius: 0,
  top: { ...DEFAULT_SIDE_BORDER },
  right: { ...DEFAULT_SIDE_BORDER },
  bottom: { ...DEFAULT_SIDE_BORDER },
  left: { ...DEFAULT_SIDE_BORDER },
};

const normalizeSide = (side) => ({
  ...DEFAULT_SIDE_BORDER,
  ...(side || {}),
});

export const normalizeBorder = (border) => {
  if (!border) {
    return { ...DEFAULT_BORDER, top: { ...DEFAULT_SIDE_BORDER }, right: { ...DEFAULT_SIDE_BORDER }, bottom: { ...DEFAULT_SIDE_BORDER }, left: { ...DEFAULT_SIDE_BORDER } };
  }
  if (typeof border === "string") {
    if (border === "none" || border.trim() === "") {
      return normalizeBorder(DEFAULT_BORDER);
    }
    return normalizeBorder({ width: 1, style: "solid", color: "#cccccc", radius: 0, advanced: false });
  }
  const base = {
    ...DEFAULT_BORDER,
    ...border,
    top: normalizeSide(border.top),
    right: normalizeSide(border.right),
    bottom: normalizeSide(border.bottom),
    left: normalizeSide(border.left),
  };
  if (!base.advanced) {
    const shared = {
      width: base.width,
      style: base.style,
      color: base.color,
      radius: base.radius,
    };
    return {
      ...base,
      top: { ...base.top, ...shared },
      right: { ...base.right, ...shared },
      bottom: { ...base.bottom, ...shared },
      left: { ...base.left, ...shared },
    };
  }
  return base;
};

const sideBorderLineToCss = (side, propertyPrefix) => {
  const normalized = normalizeSide(side);
  if (normalized.width > 0 && normalized.style !== "none") {
    return `${propertyPrefix}: ${normalized.width}px ${normalized.style} ${normalized.color};`;
  }
  return "";
};

/** Corner radii: advanced mode merges adjacent side radii per corner. */
export const borderRadiusToCss = (border) => {
  const normalized = normalizeBorder(border);
  if (normalized.advanced) {
    const topLeft = Math.max(normalized.top.radius || 0, normalized.left.radius || 0);
    const topRight = Math.max(normalized.top.radius || 0, normalized.right.radius || 0);
    const bottomRight = Math.max(normalized.bottom.radius || 0, normalized.right.radius || 0);
    const bottomLeft = Math.max(normalized.bottom.radius || 0, normalized.left.radius || 0);
    const parts = [];
    if (topLeft) {
      parts.push(`border-top-left-radius: ${topLeft}px`);
    }
    if (topRight) {
      parts.push(`border-top-right-radius: ${topRight}px`);
    }
    if (bottomRight) {
      parts.push(`border-bottom-right-radius: ${bottomRight}px`);
    }
    if (bottomLeft) {
      parts.push(`border-bottom-left-radius: ${bottomLeft}px`);
    }
    return parts.join("; ");
  }
  if (normalized.radius > 0) {
    return `border-radius: ${normalized.radius}px`;
  }
  return "";
};

export const borderLinesToCss = (border) => {
  const normalized = normalizeBorder(border);
  if (normalized.advanced) {
    return [
      sideBorderLineToCss(normalized.top, "border-top"),
      sideBorderLineToCss(normalized.right, "border-right"),
      sideBorderLineToCss(normalized.bottom, "border-bottom"),
      sideBorderLineToCss(normalized.left, "border-left"),
    ]
      .filter(Boolean)
      .join("; ");
  }
  if (!normalized.width || normalized.style === "none") {
    return "";
  }
  return `border: ${normalized.width}px ${normalized.style} ${normalized.color}`;
};

export const borderToCss = (border) => {
  const lines = borderLinesToCss(border);
  const radius = borderRadiusToCss(border);
  return [lines, radius].filter(Boolean).join("; ");
};

/** MUI sx props for canvas preview (supports per-corner radius). */
export const borderToSx = (border) => {
  const normalized = normalizeBorder(border);
  const sx = {};

  if (normalized.advanced) {
    const top = sideBorderLineToCss(normalized.top, "border-top");
    const right = sideBorderLineToCss(normalized.right, "border-right");
    const bottom = sideBorderLineToCss(normalized.bottom, "border-bottom");
    const left = sideBorderLineToCss(normalized.left, "border-left");
    if (top) {
      sx.borderTop = top.replace("border-top:", "").trim().replace(/;$/, "");
    }
    if (right) {
      sx.borderRight = right.replace("border-right:", "").trim().replace(/;$/, "");
    }
    if (bottom) {
      sx.borderBottom = bottom.replace("border-bottom:", "").trim().replace(/;$/, "");
    }
    if (left) {
      sx.borderLeft = left.replace("border-left:", "").trim().replace(/;$/, "");
    }
    const topLeft = Math.max(normalized.top.radius || 0, normalized.left.radius || 0);
    const topRight = Math.max(normalized.top.radius || 0, normalized.right.radius || 0);
    const bottomRight = Math.max(normalized.bottom.radius || 0, normalized.right.radius || 0);
    const bottomLeft = Math.max(normalized.bottom.radius || 0, normalized.left.radius || 0);
    if (topLeft) {
      sx.borderTopLeftRadius = `${topLeft}px`;
    }
    if (topRight) {
      sx.borderTopRightRadius = `${topRight}px`;
    }
    if (bottomRight) {
      sx.borderBottomRightRadius = `${bottomRight}px`;
    }
    if (bottomLeft) {
      sx.borderBottomLeftRadius = `${bottomLeft}px`;
    }
    return sx;
  }

  if (normalized.width > 0 && normalized.style !== "none") {
    sx.border = `${normalized.width}px ${normalized.style} ${normalized.color}`;
  }
  if (normalized.radius > 0) {
    sx.borderRadius = `${normalized.radius}px`;
  }
  return sx;
};

export const parseInlineStyle = (css) => {
  const style = {};
  if (!css) {
    return style;
  }
  css.split(";").forEach((rule) => {
    const [key, val] = rule.split(":").map((part) => part.trim());
    if (!key || !val) {
      return;
    }
    const camel = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    style[camel] = val;
  });
  return style;
};

export const nodePropsToCanvasStyle = (props, { defaultBackground } = {}) => {
  const style = { ...borderToSx(props?.border) };
  if (props?.padding) {
    style.padding = props.padding;
  }
  const background = props?.backgroundColor ?? defaultBackground;
  if (background !== undefined && background !== "") {
    style.backgroundColor = background;
  }
  return Object.keys(style).length ? style : undefined;
};
 